// Limon Ice (B6, B18 Y1/Y2/Y13): mobile sticky add-to-cart bar. Markup: snippets/limon-sticky-atc.liquid.
// - Visible whenever the main buy button is out of view (above or below it). The state is recomputed
//   on intersection changes, on scroll (so instant jumps such as scrollTo or anchor links are covered),
//   on scrollend, resize and load.
// - Add state: the bar's button submits the real product form via its `form` attribute.
// - "Choose size" state (only when the markup sets data-choose="true"; off since B20 #62, the bar always
//   names the size the picker shows as picked): the button is type="button", adds nothing, scrolls the
//   picker into view and focuses the first input.
// - Stays hidden while Shopify's cookie/consent banner is on screen, so it never covers it.
if (!customElements.get('limon-sticky-atc')) {
  customElements.define(
    'limon-sticky-atc',
    class LimonStickyAtc extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;
        this.sectionId = id;
        this.form = document.getElementById(this.dataset.formId);
        this.mainButton = document.getElementById(`ProductSubmitButton-${id}`);
        this.priceSource = document.getElementById(`price-${id}`);
        this.button = this.querySelector('[data-sticky-button]');
        this.price = this.querySelector('[data-sticky-price]');
        this.image = this.querySelector('[data-sticky-image]');
        this.option = this.querySelector('[data-sticky-option]');
        this.optionValue = this.querySelector('[data-sticky-option-value]');
        this.optionIndex = parseInt(this.dataset.optionIndex, 10);
        this.choosing = this.dataset.choose === 'true';
        if (!this.form || !this.mainButton || !this.button) return;

        this.update = this.update.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onPickerInteraction = this.onPickerInteraction.bind(this);

        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(this.update);
          this.io.observe(this.mainButton);
        }
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('scrollend', this.update, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        window.addEventListener('load', this.update);

        // Mirror the main button (sold out / unavailable / loading) and the main price.
        this.mo = new MutationObserver(() => this.sync());
        this.mo.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        if (this.priceSource) this.mo.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
        this.sync();
        this.setChoosing(this.choosing);
        this.update();

        this.button.addEventListener('click', (event) => {
          if (this.choosing) {
            event.preventDefault();
            this.revealPicker();
            return;
          }
          this.clickedAt = Date.now();
        });

        // A tap or keyboard selection on any option input ends the "Choose size" state, even when the
        // shopper confirms the preselected value (no change event): click, change, Space/Enter. Delegated on document because Dawn
        // replaces <variant-selects> after every change.
        document.addEventListener('click', this.onPickerInteraction);
        document.addEventListener('change', this.onPickerInteraction);
        document.addEventListener('keydown', this.onPickerInteraction);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // Add failed after a sticky tap: bring Dawn's error message (next to the main button) into view.
          this.unsubError = subscribe(PUB_SUB_EVENTS.cartError, () => {
            if (!this.clickedAt || Date.now() - this.clickedAt > 15000) return;
            this.clickedAt = 0;
            setTimeout(() => this.form.closest('product-form')?.scrollIntoView({ block: 'center' }));
          });
          this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== id) return;
            const variant = data.variant;
            if (variant && this.optionValue && !Number.isNaN(this.optionIndex)) {
              const value = (variant.options || [])[this.optionIndex];
              if (value) this.optionValue.textContent = value;
            }
            this.setChoosing(false);
            const src = variant?.featured_media?.preview_image?.src;
            if (!src || !this.image) return;
            const url = new URL(src, window.location.href);
            url.searchParams.set('width', '96');
            url.searchParams.set('height', '96');
            url.searchParams.set('crop', 'center');
            this.image.src = url.toString();
          });
        }
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.mo?.disconnect();
        this.unsubError?.();
        this.unsubVariant?.();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('scrollend', this.update);
        window.removeEventListener('resize', this.onScroll);
        window.removeEventListener('load', this.update);
        document.removeEventListener('click', this.onPickerInteraction);
        document.removeEventListener('change', this.onPickerInteraction);
        document.removeEventListener('keydown', this.onPickerInteraction);
      }

      get picker() {
        return document.getElementById(`variant-selects-${this.sectionId}`);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      // Visible = main buy button not intersecting the viewport, and no consent banner on screen.
      update() {
        if (!this.mainButton?.isConnected) return;
        const rect = this.mainButton.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const outOfView = rect.bottom <= 0 || rect.top >= viewportHeight || (rect.width === 0 && rect.height === 0);
        this.classList.toggle('is-visible', outOfView && !this.consentBannerShown());
      }

      consentBannerShown() {
        const banner = document.getElementById('shopify-pc__banner');
        if (!banner) return false;
        const rect = banner.getBoundingClientRect();
        return rect.height > 0 && getComputedStyle(banner).visibility !== 'hidden';
      }

      onPickerInteraction(event) {
        const picker = this.picker;
        const target = event.target;
        if (!picker || !(target instanceof Element) || !picker.contains(target)) return;
        if (!target.closest('input, select, label')) return;
        // Keyboard: Space/Enter on the already selected size confirms it (Chrome fires no click then).
        if (event.type === 'keydown' && event.key !== ' ' && event.key !== 'Enter') return;
        // Show the new value at once; Dawn's variantChange (after its section fetch) confirms it.
        if (event.type === 'change' && this.optionValue && !Number.isNaN(this.optionIndex)) {
          const group = picker.querySelectorAll('fieldset, .product-form__input--dropdown')[this.optionIndex];
          const value = group?.querySelector('input:checked')?.value ?? group?.querySelector('select')?.value;
          if (value) this.optionValue.textContent = value;
        }
        if (this.choosing) this.setChoosing(false);
      }

      setChoosing(choosing) {
        this.choosing = choosing;
        this.classList.toggle('is-choosing', choosing);
        this.button.type = choosing ? 'button' : 'submit';
        const label = choosing ? this.dataset.labelChoose : this.dataset.labelAdd;
        if (label && this.button.textContent.trim() !== label) this.button.textContent = label;
        if (this.option) this.option.hidden = choosing;
      }

      revealPicker() {
        const picker = this.picker;
        if (!picker) return;
        const groups = picker.querySelectorAll('fieldset, .product-form__input--dropdown');
        const group = groups[this.optionIndex] || groups[0] || picker;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const rect = group.getBoundingClientRect();
        const top = window.scrollY + rect.top - Math.max(0, (window.innerHeight - rect.height) / 2);
        window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
        group.querySelector('input, select')?.focus({ preventScroll: true });
      }

      sync() {
        const unavailable = this.mainButton.hasAttribute('disabled');
        this.classList.toggle('is-unavailable', unavailable);
        this.button.disabled = unavailable;
        if (this.mainButton.getAttribute('aria-disabled') === 'true') {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }

        const price = this.priceSource?.querySelector('.price');
        if (!price || !this.price) return;
        const items = price.querySelectorAll(
          price.classList.contains('price--on-sale') ? '.price__sale .price-item--last' : '.price__regular .price-item--regular'
        );
        const text = items.length ? items[items.length - 1].textContent.trim() : '';
        if (text && text !== this.price.textContent) this.price.textContent = text;
      }
    }
  );
}
